import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { CampaignClass, FacebookCampaign } from '@feathr/blackbox';
import { FormSummary, FormSummaryItem } from '@feathr/components';
import CampaignTypeChip from '@feathr/extender/components/CampaignTypeChip';

import AudienceStatsSummary from './AudienceStatsSummary';
import AudienceSummary from './AudienceSummary';
import AudienceTargetsSummary from './AudienceTargetsSummary';
import BudgetSummary from './BudgetSummary';
import type { ISummaryProps } from './CampaignSummary.types';
import ChannelSplitSummary from './ChannelSplitSummary';
import CreativesSummary from './CreativesSummary';
import DripEnrollmentSummary from './DripEnrollmentSummary';
import DurationSummary from './DurationSummary';
import EmailSummary from './EmailSummary';
import FiltersSummary from './FiltersSummary';
import GoalsSummary from './GoalsSummary';
import LinksSummary from './LinksSummary';
import MetadataSummary from './MetadataSummary';
import ObjectiveSummary from './ObjectiveSummary';
import OptimizationSummary from './OptimizationSummary';
import ParticipationSummary from './ParticipationSummary';
import PartnerSummary from './PartnerSummary';
import ReferralLinksSummary from './ReferralLinksSummary';
import SendSummary from './SendSummary';
import SubjectSummary from './SubjectSummary';
import TargetedCountriesSummary from './TargetedCountriesSummary';
import TemplatesSummary from './TemplatesSummary';
import TriggerSummary from './TriggerSummary';

import * as styles from './CampaignSummary.css';

function getSummaryComponents(cls: CampaignClass): Array<React.FC<ISummaryProps>> {
  switch (cls) {
    case CampaignClass.Segment:

    case CampaignClass.Search:

    case CampaignClass.Lookalike:

    case CampaignClass.SeedSegment:

    case CampaignClass.Affinity:

    case CampaignClass.EmailList:

    case CampaignClass.Omni:
      return [
        AudienceTargetsSummary,
        FiltersSummary,
        DurationSummary,
        BudgetSummary,
        ChannelSplitSummary,
        OptimizationSummary,
        CreativesSummary,
        GoalsSummary,
        PartnerSummary,
      ];

    case CampaignClass.MobileGeoFencing:

    case CampaignClass.MobileGeoFenceRetargeting:
      return [
        AudienceTargetsSummary,
        DurationSummary,
        BudgetSummary,
        OptimizationSummary,
        CreativesSummary,
        GoalsSummary,
        PartnerSummary,
      ];

    case CampaignClass.TrackedLink:
      return [LinksSummary, GoalsSummary];

    case CampaignClass.Facebook:

    case CampaignClass.EmailListFacebook:
      return [
        ObjectiveSummary,
        TargetedCountriesSummary,
        AudienceTargetsSummary,
        DurationSummary,
        BudgetSummary,
        CreativesSummary,
        GoalsSummary,
      ];

    case CampaignClass.Referral:
      return [
        DurationSummary,
        ParticipationSummary,
        TemplatesSummary,
        MetadataSummary,
        ReferralLinksSummary,
        GoalsSummary,
      ];

    case CampaignClass.PinpointEmail:
      return [
        SendSummary,
        EmailSummary,
        SubjectSummary,
        AudienceSummary,
        TemplatesSummary,
        GoalsSummary,
        AudienceStatsSummary,
      ];

    case CampaignClass.Drip:
      return [SendSummary, DripEnrollmentSummary];

    case CampaignClass.SmartPinpointEmail:
      return [
        SendSummary,
        EmailSummary,
        SubjectSummary,
        AudienceSummary,
        TriggerSummary,
        TemplatesSummary,
        GoalsSummary,
        AudienceStatsSummary,
      ];

    case CampaignClass.AutoPinpointEmail:
      return [
        SendSummary,
        EmailSummary,
        SubjectSummary,
        AudienceSummary,
        TriggerSummary,
        TemplatesSummary,
        GoalsSummary,
      ];

    default:
      return [GoalsSummary];
  }
}

function CampaignSummary({
  campaign,
  validationErrors = {},
  showMinimal = false,
  layout = 'horizontal',
  className,
  creatives,
}: Readonly<ISummaryProps>): JSX.Element {
  const { t } = useTranslation();

  const minimalProps = (
    <>
      <FormSummaryItem
        errors={validationErrors.name}
        key={'campaign-name'}
        label={t('Campaign name')}
        name={'summary_campaign_name'}
        value={campaign.name}
      />
      <FormSummaryItem
        key={'campaign-type'}
        label={t('Type')}
        name={'summary_campaign_type'}
        value={
          <CampaignTypeChip
            cls={campaign.get('_cls')}
            isMonetization={campaign.isMonetization}
            truncate={false}
          />
        }
      />
    </>
  );

  if (showMinimal) {
    return <FormSummary layout={'vertical'}>{minimalProps}</FormSummary>;
  }

  return (
    <FormSummary className={className} layout={layout}>
      {campaign instanceof FacebookCampaign && validationErrors.facebook?.length ? (
        <FormSummaryItem
          errors={validationErrors.facebook}
          key={'facebook-integration-status'}
          label={t('Meta Integration Status')}
          value={t('Error')}
        />
      ) : (
        <></>
      )}
      {minimalProps}

      <>
        {getSummaryComponents(campaign.get('_cls')).map((SummaryComponent) => {
          return (
            <SummaryComponent
              campaign={campaign}
              creatives={creatives}
              key={SummaryComponent.displayName}
              validationErrors={validationErrors}
            />
          );
        })}
      </>
      <>
        {validationErrors.account?.map((msg, index) => (
          <div className={styles.error} key={index}>
            {msg}
          </div>
        ))}
      </>
    </FormSummary>
  );
}

export default observer(CampaignSummary);
